import type { Cell, PlanView } from "./plan";
import { ALL_DEPTHS, ALL_SIDES, type Depth, type Layout, type Side } from "./search";
import type { CompactPlan } from "./types";

const ZONE_EDGE = 1 / 3;

type Slot = { row: number; column: number };

/** Géométrie seule (catalogue) : o siège · h PMR · n neutralisé · . vide */
const GEOMETRY: Record<string, Cell> = { o: "free", h: "pmr", n: "blocked", ".": "empty" };

const ratio = (index: number, total: number) => (total > 1 ? index / (total - 1) : 0.5);

function inDepth(rowIndex: number, rowCount: number, depths: Depth[]) {
  if (depths.length >= ALL_DEPTHS.length) return true;
  const position = ratio(rowIndex, rowCount);
  const band = position <= ZONE_EDGE ? "front" : position >= 1 - ZONE_EDGE ? "back" : "middle";
  return depths.includes(band as Depth);
}

function inSide(position: number, width: number, sides: Side[]) {
  if (sides.length >= ALL_SIDES.length) return true;
  const relative = ratio(position, width);
  const central = relative >= ZONE_EDGE && relative <= 1 - ZONE_EDGE;
  return sides.includes(central ? "center" : "sides");
}

/**
 * Rend une salle du catalogue en surlignant la zone demandée, puis la place que
 * prendrait le groupe. Le tout sur la vraie géométrie, jamais sur un schéma abstrait.
 */
export function previewHall(
  plan: CompactPlan,
  options: { depth: Depth[]; side: Side[]; people: number; layout: Layout },
): { view: PlanView; placed: number } {
  // les allées horizontales ne comptent pas comme des rangées pour les zones
  const seatRowIndexes = plan.rows
    .map((row, index) => ({ index, hasSeats: [...row.cells].some((char) => char !== ".") }))
    .filter((row) => row.hasSeats)
    .map((row) => row.index);
  const depthOf = new Map(seatRowIndexes.map((index, position) => [index, position]));

  const rows = plan.rows.map((row) => [...row.cells]);
  const rowCount = seatRowIndexes.length;

  const zone: boolean[][] = rows.map((cells, rowIndex) => {
    const seats = cells
      .map((char, column) => ({ char, column }))
      .filter((cell) => cell.char !== ".");
    const positions = new Map(seats.map((seat, position) => [seat.column, position]));

    return cells.map((char, column) => {
      if (char === "." || char === "n") return false;
      const position = positions.get(column) ?? 0;
      const depthIndex = depthOf.get(rowIndex) ?? 0;
      return (
        inDepth(depthIndex, rowCount, options.depth) && inSide(position, seats.length, options.side)
      );
    });
  });

  const group = pickGroup(rows, zone, options);
  const picked = new Set(group.map((slot) => `${slot.row}:${slot.column}`));

  // On rend l'ordre d'affichage (écran en bas) avant de tracer les bords, sinon
  // le haut et le bas de la zone seraient inversés.
  const order = rows.map((_, index) => index).reverse();
  const zoned = options.depth.length < ALL_DEPTHS.length || options.side.length < ALL_SIDES.length;

  const view: PlanView = {
    columns: plan.columns,
    rows: order.map((rowIndex, displayRow) => ({
      name: plan.rows[rowIndex].name,
      gap: !depthOf.has(rowIndex),
      cells: rows[rowIndex].map((char, column): Cell => {
        if (picked.has(`${rowIndex}:${column}`)) return "picked";
        return GEOMETRY[char] ?? "empty";
      }),
      // Seules les places du groupe sont cerclées : la zone, elle, se lit déjà au
      // contraste entre sièges allumés et sièges atténués.
      marks: rows[rowIndex].map((_, column) => {
        if (!picked.has(`${rowIndex}:${column}`)) {
          return zoned && !zone[rowIndex][column] ? "is-dim" : "";
        }
        const inGroup = (row: number | undefined, col: number) =>
          row === undefined ? false : picked.has(`${row}:${col}`);
        return [
          "in-pick",
          inGroup(order[displayRow - 1], column) ? "" : "zt",
          inGroup(order[displayRow + 1], column) ? "" : "zb",
          inGroup(rowIndex, column + 1) ? "" : "zr",
          inGroup(rowIndex, column - 1) ? "" : "zl",
        ]
          .filter(Boolean)
          .join(" ");
      }),
    })),
  };

  return { view, placed: group.length };
}

/** Suites de sièges libres contigus dans la zone, par rangée. */
function runsOf(cells: string[], allowed: boolean[]) {
  const runs: number[][] = [];
  let current: number[] = [];

  cells.forEach((char, column) => {
    const usable = char !== "." && char !== "n" && allowed[column];
    if (!usable) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }
    if (current.length && column !== current[current.length - 1] + 1) {
      runs.push(current);
      current = [];
    }
    current.push(column);
  });

  if (current.length) runs.push(current);
  return runs;
}

const centred = (run: number[], size: number, middle: number) => {
  let best: number[] = [];
  let bestGap = Infinity;
  for (let start = 0; start + size <= run.length; start += 1) {
    const slice = run.slice(start, start + size);
    const gap = Math.abs(slice.reduce((total, column) => total + column, 0) / size - middle);
    if (gap < bestGap) {
      bestGap = gap;
      best = slice;
    }
  }
  return best;
};

function pickGroup(
  rows: string[][],
  zone: boolean[][],
  { people, layout }: { people: number; layout: Layout },
): Slot[] {
  if (people < 1) return [];
  const middle = (rows[0]?.length ?? 0) / 2;

  /** À rangée égale, on montre le placement le plus central : c'est l'exemple attendu. */
  const bestInRow = (rowIndex: number, size: number) => {
    let best: number[] = [];
    let bestGap = Infinity;
    for (const run of runsOf(rows[rowIndex], zone[rowIndex])) {
      if (run.length < size) continue;
      const slice = centred(run, size, middle);
      const gap = Math.abs(slice.reduce((total, column) => total + column, 0) / size - middle);
      if (gap < bestGap) {
        bestGap = gap;
        best = slice;
      }
    }
    return best;
  };

  /**
   * Rangées de la zone, de la plus centrale vers les bords : l'exemple doit se lire
   * au milieu de ce qui a été retenu, pas collé au fond de salle.
   */
  const rowsFromCentre = (needsPair: boolean) => {
    const inZone = rows
      .map((_, index) => index)
      .filter((index) => zone[index]?.some(Boolean) && (!needsPair || index >= 1));
    if (inZone.length === 0) return [];

    const centre = (inZone[0] + inZone[inZone.length - 1]) / 2;
    return [...inZone].sort((a, b) => Math.abs(a - centre) - Math.abs(b - centre));
  };

  const onOneRow = (size: number) => {
    for (const rowIndex of rowsFromCentre(false)) {
      const slice = bestInRow(rowIndex, size);
      if (slice.length) return slice.map((column) => ({ row: rowIndex, column }));
    }
    return [];
  };

  if (people === 1 || layout === "row") return onOneRow(people);

  const front = Math.ceil(people / 2);
  const back = Math.floor(people / 2);

  if (layout === "two-rows") {
    for (const rowIndex of rowsFromCentre(true)) {
      const upper = bestInRow(rowIndex, front);
      if (!upper.length) continue;

      const lowerRuns = runsOf(rows[rowIndex - 1], zone[rowIndex - 1]);
      const behind = upper.slice(0, back);
      if (!lowerRuns.some((run) => behind.every((column) => run.includes(column)))) continue;

      return [
        ...upper.map((column) => ({ row: rowIndex, column })),
        ...behind.map((column) => ({ row: rowIndex - 1, column })),
      ];
    }
  }

  return onOneRow(people);
}
